import React, { useEffect, useMemo, useRef, useState } from "react"

import { css } from "@emotion/react"
import { Divider, type RefSelectProps, Select } from "antd"
import awesomeDebouncePromise from "awesome-debounce-promise"
import { stringify } from "flatted"
import {
    type Control,
    Controller,
    type DeepMap,
    type FieldError,
    type FieldValues,
    type UseFormRegister,
    type UseFormSetValue,
    type UseFormUnregister,
} from "react-hook-form"

import type { IIndexable } from "@common-shared/types/general.types"

import type {
    ISelectRawOption,
    ISelectRenderedOption,
} from "@front-shared/types/objects.types"

import { LookupHelper } from "../helpers/lookup.helper"
import { useScrollErroredInputIntoView } from "../hooks/scrollErroredInputtIntoView.hook"
import { FloatingLabelElement } from "./floatingLabel.element"
import { InputErrorElement } from "./inputError.element"
import { PrimaryText } from "./primaryText.element"

import searchIcon from "@front-shared/assets/images/icons/arrow-down-dark2.svg"

export const SelectElement: React.FunctionComponent<
    ISelectElementProps
> = props => {
    const containerRef = useRef<HTMLDivElement>(null)
    const selectRef = useRef<RefSelectProps>(null)

    useScrollErroredInputIntoView(
        props.name,
        containerRef,
        props.reactHookFormErrors,
        props.ignoreScrollIntoViewOnError,
    )

    const [searchedItems, setSearchedItems] = useState<any[]>([])
    const [options, setOptions] = useState<ISelectRenderedOption[]>([])
    const [defaultProcessed, setDefaultProcessed] = useState<boolean>(false)

    const elementFullName = useMemo<string>(() => {
        return `${props.fieldGroupName ? `${props.fieldGroupName}.` : ""}${
            props.name
        }`
    }, [props.name, props.fieldGroupName])

    const debouncedFetcher = useMemo(() => {
        if (!props.dataFetcher) {
            return null
        }

        return awesomeDebouncePromise(props.dataFetcher, 500)
    }, [])

    const selectHasError = useMemo<boolean>(() => {
        return !!props.reactHookFormErrors?.[props.name]
    }, [props.reactHookFormErrors?.[props.name]])

    useEffect(() => {
        if (debouncedFetcher && props.initialFetch) {
            void onSearch("")
        }
    }, [debouncedFetcher])

    useEffect(() => {
        if (props.reactHookFormRegister) {
            props.reactHookFormRegister(
                elementFullName,
                props.validations || {},
            )
        }

        return () => {
            props.reactHookFormUnregister?.(elementFullName)
        }
    }, [props.reactHookFormRegister])

    useEffect(() => {
        if (!props.renderedOptions) {
            return
        }

        setOptions(props.renderedOptions)
    }, [props.renderedOptions])

    // Once searched items change - set them as options
    useEffect(() => {
        if (!props.itemRenderer || !debouncedFetcher) {
            return
        }

        setOptions(searchedItems.map(props.itemRenderer))
    }, [searchedItems])

    // Process default value, make sure to run it only once and once options are set
    useEffect(() => {
        if (
            (!options.length && !props.options?.length) ||
            !props.defaultValue ||
            defaultProcessed
        ) {
            return
        }

        const timeoutId = setTimeout(() => {
            onSelected(props.defaultValue)

            setDefaultProcessed(true)
        })

        return () => {
            try {
                clearTimeout(timeoutId)
            } catch (e) {}
        }
    }, [props.defaultValue, stringify(options)])

    const notFoundContent: React.JSX.Element | null = useMemo(
        () =>
            LookupHelper.getNotFoundContent(
                props.notFoundTitle,
                props.notFoundSubtitle,
                props.notFoundAction,
                undefined,
                !props.ignoreBlurOnNotFoundAction && selectRef.current
                    ? selectRef.current
                    : undefined,
            ),

        [
            props.notFoundTitle,
            props.notFoundSubtitle,
            props.notFoundAction,
            selectRef.current,
            props.ignoreBlurOnNotFoundAction,
        ],
    )

    const onSearch = async (search: string) => {
        if (debouncedFetcher) {
            setSearchedItems(await debouncedFetcher(search))
        }

        if (props.onChange) {
            props.onChange(search)
        }
    }

    const onSelected = (selectedDisplayValue: string) => {
        let selectedOption: any = options.find(
            item => item.displayValue === selectedDisplayValue,
        )

        if (!options.length && props.options) {
            selectedOption = props.options.find(
                item => item.value === selectedDisplayValue,
            )
        }

        if (!selectedOption) {
            return
        }

        if (props.onSelected) {
            props.onSelected(selectedOption.value)
        }

        if (
            props.reactHookFormSet &&
            typeof selectedOption.value === "string"
        ) {
            props.reactHookFormSet(elementFullName, selectedOption.value, {
                shouldValidate: !!props.shouldValidate,
            })
        } else if (typeof selectedOption.value === "undefined") {
            onClear()
        }
    }

    const initialValue = useMemo(() => {
        if (!props.defaultValue) {
            return undefined
        }

        if (Array.isArray(props.defaultValue)) {
            return props.defaultValue.map(item =>
                props.itemRenderer
                    ? props.itemRenderer(item).displayValue
                    : "No itemRenderer",
            )
        }

        return props.defaultValue
    }, [props.defaultValue])

    const renderPrefixIcon = (
        icon?: React.JSX.Element | string,
    ): React.JSX.Element | null => {
        if (!icon) {
            return null
        }

        if (typeof icon !== "string") {
            return icon
        } else {
            return <img src={icon} alt="Select" title="Select" />
        }
    }

    const onClear = () => {
        if (!props.allowClear) {
            return
        }

        props.onSelected?.(undefined)

        props.reactHookFormSet?.(elementFullName, undefined, {
            shouldValidate: !!props.shouldValidate,
        })
    }

    return (
        <div
            ref={containerRef}
            className={`
                relative
                ${props.transparent ? "ant-select-transparent" : ""}
                ${props.short ? "ant-select-short" : ""}
                ${props.inlined ? "ant-select-inlined" : ""}
                ${
                    props.selectResultPrefixIcon
                        ? "ant-select-result-with-prefix"
                        : ""
                }
                ${props.className}
            `}
        >
            {props.label && (
                <FloatingLabelElement
                    label={props.label}
                    isFloated={true}
                    withRequiredStar={
                        props.withRequiredStar && props.validations?.required
                    }
                    tooltip={props.tooltip}
                />
            )}

            {/*prefix icon for selected item (not for dropdown values)*/}
            {props.selectResultPrefixIcon && (
                <div
                    className="absolute"
                    css={[
                        css({
                            left: 16,
                            marginTop: props.label ? 14 : 0,
                            top: 10,
                            zIndex: 100,
                        }),
                    ]}
                >
                    {renderPrefixIcon(props.selectResultPrefixIcon)}
                </div>
            )}

            {(() => {
                const commonProps = {
                    ref: selectRef,
                    size: props.size || "large",
                    placeholder: props.placeholder,
                    css: [styles.select],
                    className: `${props.selectClassName || ""} ${
                        selectHasError &&
                        (!props.hideErrors || props.forceHighlightOnError)
                            ? "errored"
                            : ""
                    }`,
                    disabled: props.disabled,
                    defaultValue: initialValue,
                    onSearch: props.dataFetcher ? onSearch : undefined,
                    showSearch: !!props.dataFetcher,
                    onSelect: onSelected,
                    onDeselect: props.onDeselected,
                    onClear: onClear,
                    dropdownMatchSelectWidth: false,
                    allowClear: !!props.allowClear,
                    mode: props.mode,
                    options: props.options,
                    suffixIcon: (
                        <img src={searchIcon} alt="Select" title="Select" />
                    ),
                    notFoundContent: notFoundContent || undefined,
                    // https://github.com/ant-design/ant-design/issues/40205
                    // https://github.com/ant-design/ant-design/issues/34048
                    optionLabelProp: "children",
                    dropdownRender: !props.getCustomDropdownContent
                        ? undefined
                        : (menu: React.JSX.Element) => {
                              return (
                                  <>
                                      {menu}
                                      <Divider style={{ margin: "8px 0" }} />
                                      {props.getCustomDropdownContent?.()}
                                  </>
                              )
                          },
                    placement: props.placement,
                    getPopupContainer:
                        props.getPopupContainer ||
                        (triggerNode =>
                            triggerNode.parentElement || document.body),
                    dropdownStyle: props.dropdownStyle,
                    // Force dropdown to respect placement in Ant Design v4
                    dropdownAlign: props.placement?.includes("top")
                        ? { points: ["bl", "tl"] }
                        : { points: ["tl", "bl"] },
                }

                const mappedOptions = options.map((item, index) => (
                    <Select.Option
                        key={index}
                        value={item.displayValue}
                        title={item.displayValue}
                    >
                        <div className="flex items-center h-full">
                            {item.prefixIcon || props.prefixIcon ? (
                                <div
                                    className="flex items-center"
                                    css={[styles.prefixIcon]}
                                >
                                    {renderPrefixIcon(
                                        item.prefixIcon || props.prefixIcon,
                                    )}
                                </div>
                            ) : null}

                            <div
                                className="truncate"
                                css={[styles.optionWrapper]}
                            >
                                {item.dropdownDisplayValue || item.displayValue}

                                {item.dropdownDisplayValueAdditional ? (
                                    <div className="dropdown-value-additional flex-1">
                                        <PrimaryText
                                            css={styles.optionWrapper}
                                            typography="caption"
                                        >
                                            {typeof item.dropdownDisplayValueAdditional ===
                                            "string"
                                                ? item.dropdownDisplayValueAdditional
                                                      .split("<br />")
                                                      .join("\n")
                                                : item.dropdownDisplayValueAdditional}
                                        </PrimaryText>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </Select.Option>
                ))

                return props.reactHookFormControl ? (
                    <Controller
                        control={props.reactHookFormControl}
                        rules={props.validations}
                        name={elementFullName}
                        shouldUnregister={true}
                        render={({ field }) => (
                            <Select {...field} {...commonProps}>
                                {mappedOptions}
                            </Select>
                        )}
                    />
                ) : (
                    <Select {...commonProps} value={props.externalValue}>
                        {mappedOptions}
                    </Select>
                )
            })()}

            {selectHasError &&
                !props.hideErrors &&
                props.reactHookFormErrors && (
                    <InputErrorElement
                        text={
                            (
                                props.reactHookFormErrors[
                                    props.name
                                ] as FieldError
                            ).message || "Error"
                        }
                    />
                )}
        </div>
    )
}

const styles = {
    select: css({
        width: "100%",
        borderRadius: 6,
        fontFamily: "PPMori, sans-serif",
    }),
    prefixIcon: css({
        marginRight: 10,
    }),
    optionWrapper: css({
        marginTop: 4,
        width: "100%",
    }),
}

interface ISelectElementProps {
    name: string
    fieldGroupName?: string
    className?: string
    selectClassName?: string
    label?: string
    withRequiredStar?: boolean
    transparent?: boolean
    short?: boolean
    inlined?: boolean
    tooltip?: string
    externalValue?: string
    initialFetch?: boolean
    onChange?: (search: string) => void
    onSelected?: (item: any) => void
    onDeselected?: (item: any) => void
    dataFetcher?: (search?: string) => Promise<any[]> | any[]
    itemRenderer?: (item: any) => ISelectRenderedOption
    defaultValue?: any
    placeholder?: string
    disabled?: boolean
    size?: "large" | "small"
    mode?: "multiple" | "tags"
    reactHookFormRegister?: UseFormRegister<FieldValues>
    reactHookFormUnregister?: UseFormUnregister<FieldValues>
    reactHookFormSet?: UseFormSetValue<FieldValues>
    shouldValidate?: boolean
    reactHookFormErrors?: DeepMap<FieldValues, FieldError>
    reactHookFormControl?: Control
    validations?: IIndexable // Validations object
    hideErrors?: boolean
    forceHighlightOnError?: boolean
    renderedOptions?: ISelectRenderedOption[]
    options?: ISelectRawOption[]
    getCustomDropdownContent?: () => React.JSX.Element
    prefixIcon?: string | React.JSX.Element
    selectResultPrefixIcon?: string | React.JSX.Element
    ignoreScrollIntoViewOnError?: boolean
    notFoundTitle?: string
    notFoundSubtitle?: string
    notFoundAction?: () => React.JSX.Element
    ignoreBlurOnNotFoundAction?: boolean
    allowClear?: boolean
    placement?:
        | "topLeft"
        | "topRight"
        | "bottomLeft"
        | "bottomRight"
        | undefined
    getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement
    dropdownStyle?: React.CSSProperties
}
